import { BrandKitTemplateResponse, BrandTemplateCreateRequestBody } from '@goldcast/api/content';
import { isSplitsInTimelineEnabledPure } from '@goldcast/player-config/src/utils/clipUtils';
import { Clip, ClipMetadata } from '@goldcast/player-config/src/types/clip-types';
import { getClipProcessingState, getClipUnavailableState } from '../../MagicLayout/magicLayoutUtils';
import { PreviewTemplate, TemplateDefault } from '../types';
import { getCurrentScene } from '../../ClipPlayer/ClipNewTimeline/clipUpdateDataUtils/sceneUtils';
import { currentClip } from '@/stores/clip';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { getVideoSegmentById } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/utils';

export function mapBrandedTemplatesResponse(
  clipId: string,
  responseResults: BrandKitTemplateResponse[]
): PreviewTemplate[] {
  const currentClipState = currentClip.getSnapshot()[clipId];
  return (responseResults || [])
    .map(item => {
      const [isUnavailable, message, reason] = getClipUnavailableState(item.layout, currentClipState);
      return {
        subtitle: item.config.subtitle,
        id: item.id,
        captions: currentClipState.asset_metadata.captions,
        layout: item.layout,
        size: item.size,
        intro: item.config.intro,
        introDuration: item.config.introDuration,
        outro: item.config.outro,
        outroDuration: item.config.outroDuration,
        title: item.title as string,
        position: item.config.position !== undefined ? item.config.position : 0.5,
        font_location: item.config.captions?.[0]?.style.regular_style.font_location,
        magicLayout: {
          backgroundColor: item.config.background_color,
          textColor: item.config.captions?.[0]?.style.regular_style.font_color,
          showSpeakerLabels: !!item.config.show_speaker_labels,
          backgroundImage: item.config.background_image
        },
        isUnavailable,
        message,
        reason,
        isProcessing: getClipProcessingState(item.layout, currentClipState),
        isDefault: !item.organization,
        tags: item.tags,
        highlight_type: item.config.captions?.[0]?.style.highlight_style?.highlight_type,
        word_highlight_color: item.config.captions?.[0]?.style.highlight_style?.background_color,
        text_highlight_color: item.config.captions?.[0]?.style.highlight_style?.font_color,
        createdAt: item.created_at,
        default: item.config.default as TemplateDefault,
        caption_positions: item.config.caption_positions,
        caption_styles: item.config.caption_styles,
        hide_borders: item.config.hide_borders !== undefined ? item.config.hide_borders : !item.config.subtitle,
        speaker_label_styles: item.config.speaker_label_styles,
        fit: item.config.fit || 'FIT'
      };
    })
    .sort((a, b) => sortTemplates(a, b));
}

export function mapCurrentClipToTemplate(clipId: string): PreviewTemplate {
  const clip = currentClip.getSnapshot()[clipId];
  const currentClipMetadata = clip.asset_metadata;

  // Determine layout and fit based on current scene
  let layout = currentClipMetadata.layout;
  let fit = currentClipMetadata.fit;

  const currentScene = getCurrentScene(clip);
  if (currentScene) {
    layout = currentScene.details.layout;
    fit = currentScene.details.fitMode;
  }

  const { intro, outro, introDuration, outroDuration } = getIntroOutroFromTemplate(clip);

  return {
    layout,
    fit,
    subtitle: currentClipMetadata.subtitle,
    captions: currentClipMetadata.captions,
    size: currentClipMetadata.size,
    intro,
    introDuration,
    outro,
    outroDuration,
    font_location: currentClipMetadata.font_location,
    highlight_type: currentClipMetadata.highlight_type,
    word_highlight_color: currentClipMetadata.word_highlight_color,
    caption_positions: currentClipMetadata.caption_positions,
    caption_styles: currentClipMetadata.caption_styles,
    hide_borders: currentClipMetadata.hide_borders,
    text_highlight_color: currentClipMetadata.text_highlight_color,
    title: '',
    magicLayout: currentClipMetadata.magicLayout,
    isProcessing: false,
    isUnavailable: false,
    reason: null,
    message: null,
    isDefault: false,
    speaker_label_styles: currentClipMetadata.speaker_label_styles
  };
}

export function getIntroOutroFromTemplate(clip: Clip): {
  intro: string | undefined;
  outro: string | undefined;
  introDuration: number | undefined;
  outroDuration: number | undefined;
} {
  const isSplitsEnabled = isSplitsInTimelineEnabledPure({
    clip: clip,
    isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
  });
  const currentClipMetadata = clip.asset_metadata;
  let intro = currentClipMetadata.intro;
  let outro = currentClipMetadata.outro;
  let introDuration = currentClipMetadata.introDuration;
  let outroDuration = currentClipMetadata.outroDuration;
  if (isSplitsEnabled && currentClipMetadata.player_config) {
    const introSegment = getVideoSegmentById(currentClipMetadata.player_config, 'intro');
    const outroSegment = getVideoSegmentById(currentClipMetadata.player_config, 'outro');
    if (introSegment) {
      intro = introSegment.segment_metadata?.src;
      introDuration = introSegment.segment_metadata?.maxVideoDuration;
    }
    if (outroSegment) {
      outro = outroSegment.segment_metadata?.src;
      outroDuration = outroSegment.segment_metadata?.maxVideoDuration;
    }
  }

  return { intro, outro, introDuration, outroDuration };
}
export function getSaveTemplatePayload(template: PreviewTemplate, tags: string[]): BrandTemplateCreateRequestBody {
  const templateCaptions = [
    {
      style: {
        regular_style: {
          font_color: template.magicLayout?.textColor,
          font_location: template.font_location
        },
        highlight_style: {
          highlight_type: template.highlight_type,
          background_color: template.word_highlight_color,
          font_color: template.text_highlight_color
        }
      }
    }
  ];

  return {
    config: {
      background_color: template.magicLayout?.backgroundColor,
      background_image: template.magicLayout?.backgroundImage,
      show_speaker_labels: template.magicLayout?.showSpeakerLabels,
      captions: templateCaptions,
      intro: template.intro,
      introDuration: template.introDuration,
      outro: template.outro,
      outroDuration: template.outroDuration,
      subtitle: template.subtitle,
      caption_positions: template.caption_positions,
      caption_styles: template.caption_styles,
      hide_borders: template.hide_borders,
      speaker_label_styles: template.speaker_label_styles,
      fit: template.fit
    },
    layout: template.layout,
    size: template.size,
    tags: tags,
    title: template.title
  };
}

// If you are changing this, inform BE of the change as well
export const propertiesToCompareV1: (keyof Pick<
  ClipMetadata,
  | 'subtitle'
  | 'captions'
  | 'layout'
  | 'size'
  | 'intro'
  | 'introDuration'
  | 'outro'
  | 'outroDuration'
  | 'magicLayout'
  | 'font_location'
  | 'highlight_type'
  | 'word_highlight_color'
  | 'text_highlight_color'
  | 'caption_positions'
  | 'caption_styles'
  | 'hide_borders'
  | 'speaker_label_styles'
>)[] = [
  'subtitle',
  'captions',
  'layout',
  'size',
  'intro',
  'introDuration',
  'outro',
  'outroDuration',
  'magicLayout',
  'font_location',
  'highlight_type',
  'word_highlight_color',
  'text_highlight_color',
  'caption_positions',
  'caption_styles',
  'hide_borders',
  'speaker_label_styles'
];

function sortTemplates(a: PreviewTemplate, b: PreviewTemplate): number {
  const aIsProcessingOrUnavailable = a.isProcessing || a.isUnavailable;
  const bIsProcessingOrUnavailable = b.isProcessing || b.isUnavailable;
  // Check if only one of the objects has isProcessing and isUnavailable false
  if (aIsProcessingOrUnavailable !== bIsProcessingOrUnavailable) {
    return !a.isProcessing && !a.isUnavailable ? -1 : 1;
  }
  if (aIsProcessingOrUnavailable || bIsProcessingOrUnavailable) {
    return a.layout === 'SPEAKER' ? -1 : 1;
  }
  if (a.position !== b.position) {
    return (a.position || 0) - (b.position || 0); // Sort by position
  }
  // If position is equal (for user-created ones)
  const createdAtComparison = new Date(a.createdAt!).getTime() - new Date(b.createdAt!).getTime();
  return createdAtComparison !== 0 ? -createdAtComparison : 0;
}
